/**
 * Hi Tapes — home-pickup booker front end (Shopify asset).
 * Split like the rest of the build: a PURE, jsc-testable namespace (HiTapesBooker) for the
 * calendar model / validation / payload / error mapping, and a DOM+fetch controller that
 * only runs in the browser. The controller holds no secrets beyond the endpoint URL +
 * request_token, which it reads from data- attributes on the mount element.
 *
 * Talks to the GAS web app (see gas/README):
 *   GET  ENDPOINT?action=availability&token=TOKEN            -> availability
 *   POST ENDPOINT  (Content-Type text/plain, JSON body)      -> createBooking
 * The text/plain content type is deliberate: it keeps the POST a CORS "simple request" so
 * GAS never has to answer a preflight it can't.
 */
(function (global) {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var REQUIRED = ['name', 'email', 'phone', 'address', 'postcode', 'city'];
  var MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  var WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
  var WEEKDAYS_FULL = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag']; // Mon-based

  // ---------------------------------------------------------------------------
  // Pure helpers (unit-tested under jsc — no DOM, no clock)
  // ---------------------------------------------------------------------------
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function iso(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d); }

  // Mon-based month grid: weeks of 7 cells, each a 'YYYY-MM-DD' or null (padding).
  function monthGrid(year, month) {
    var firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // 0=Sun..6=Sat
    var lead = (firstDow + 6) % 7;                                     // shift to Mon=0
    var days = new Date(Date.UTC(year, month, 0)).getUTCDate();
    var cells = [];
    var i, d, w;
    for (i = 0; i < lead; i++) cells.push(null);
    for (d = 1; d <= days; d++) cells.push(iso(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    var weeks = [];
    for (w = 0; w < cells.length; w += 7) weeks.push(cells.slice(w, w + 7));
    return weeks;
  }

  // Inclusive list of {year,month} from today's month to horizonEnd's month.
  function monthsInRange(today, horizonEnd) {
    var y = +today.slice(0, 4), m = +today.slice(5, 7);
    var ey = +horizonEnd.slice(0, 4), em = +horizonEnd.slice(5, 7);
    var out = [];
    while ((y < ey || (y === ey && m <= em)) && out.length < 24) {
      out.push({ year: y, month: m });
      m++; if (m > 12) { m = 1; y++; }
    }
    return out;
  }

  // Index a getAvailability response into fast lookups for the calendar.
  function indexAvailability(resp) {
    resp = resp || {};
    var sel = {}, blocked = {};
    (resp.selectableDates || []).forEach(function (s) { sel[s.date] = s; });
    (resp.blockedDates || []).forEach(function (date) { blocked[date] = true; });
    return {
      sel: sel, blocked: blocked,
      today: resp.today,
      minDate: resp.meta && resp.meta.minDate,
      horizonEnd: resp.meta && resp.meta.horizonEnd,
      windows: (resp.settings && resp.settings.windows) || []
    };
  }

  function classifyDate(dateStr, model) {
    if (!dateStr) return 'empty';
    if (model.sel[dateStr]) return 'selectable';
    if (model.blocked[dateStr]) return 'blocked';
    return 'muted';
  }

  // Mirror the server's required-field + email checks so we fail fast client-side.
  function validateContact(v) {
    v = v || {};
    var missing = REQUIRED.filter(function (k) { return !v[k] || !String(v[k]).trim(); });
    var invalidEmail = !!(v.email && !EMAIL_RE.test(String(v.email).trim()));
    return { ok: missing.length === 0 && !invalidEmail, missing: missing, invalidEmail: invalidEmail };
  }

  function buildBookingPayload(v, date, token, attemptId) {
    v = v || {};
    return {
      action: 'createBooking', request_token: token, attempt_id: attemptId, date: date,
      name: v.name, email: v.email, phone: v.phone, address: v.address,
      postcode: v.postcode, city: v.city,
      order_number: v.order_number || '', notes: v.notes || ''
    };
  }

  // 'YYYY-MM-DD' -> 'vrijdag 3 juli 2026' (Dutch, no leading zero on the day).
  function formatDateNL(isoStr) {
    var y = +isoStr.slice(0, 4), m = +isoStr.slice(5, 7), d = +isoStr.slice(8, 10);
    var dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();   // 0=Sun
    return WEEKDAYS_FULL[(dow + 6) % 7] + ' ' + d + ' ' + MONTHS[m - 1] + ' ' + y;
  }

  // '12:00-15:00' -> 'We komen naar verwachting tussen 12:00 en 15:00 langs.'
  // Deliberately non-committal ("naar verwachting"): the booking is a request we may still
  // confirm/adjust by phone, so the copy never promises a hard time.
  function windowSentenceNL(windowLabel) {
    var p = String(windowLabel || '').split('-');
    return p.length === 2 ? ('We komen naar verwachting tussen ' + p[0] + ' en ' + p[1] + ' langs.') : String(windowLabel || '');
  }

  // Index of the first month in `months` that actually contains a selectable date — so we
  // open the calendar on a useful month instead of an all-greyed current month.
  function firstSelectableMonth(months, sel) {
    var keys = Object.keys(sel || {});
    for (var i = 0; i < months.length; i++) {
      var prefix = months[i].year + '-' + pad2(months[i].month);
      for (var k = 0; k < keys.length; k++) if (keys[k].slice(0, 7) === prefix) return i;
    }
    return 0;
  }

  var ERRORS = {
    'anchor-full': 'Sorry — die dag is net volgeboekt. Kies een andere gemarkeerde dag.',
    'capacity-full': 'Sorry — die dag heeft net de maximale capaciteit bereikt. Kies een andere gemarkeerde dag.',
    'near-anchor': 'Die dag is niet meer te kiezen. Kies een gemarkeerde dag.',
    'ineligible': 'Die dag is niet beschikbaar. Kies een gemarkeerde dag.',
    'missing_fields': 'Vul alle verplichte velden in.',
    'invalid_email': 'Vul een geldig e-mailadres in.',
    'forbidden': 'De planner is niet juist ingesteld. Neem contact met ons op.',
    'busy': 'Het is even druk — probeer het zo nog een keer.',
    'bad_json': 'Er ging iets mis. Probeer het opnieuw.',
    'method_not_allowed': 'Er ging iets mis. Probeer het opnieuw.',
    'network': 'We konden de boekingsservice niet bereiken. Controleer je verbinding en probeer het opnieuw.'
  };
  function friendlyError(reason) { return ERRORS[reason] || 'Er ging iets mis. Probeer het opnieuw.'; }

  var Booker = {
    iso: iso, monthGrid: monthGrid, monthsInRange: monthsInRange, indexAvailability: indexAvailability,
    classifyDate: classifyDate, validateContact: validateContact, buildBookingPayload: buildBookingPayload,
    friendlyError: friendlyError, formatDateNL: formatDateNL, windowSentenceNL: windowSentenceNL,
    firstSelectableMonth: firstSelectableMonth, MONTHS: MONTHS, WEEKDAYS: WEEKDAYS, REQUIRED: REQUIRED
  };
  global.HiTapesBooker = Booker;
  if (typeof module !== 'undefined' && module.exports) module.exports = Booker;

  // ---------------------------------------------------------------------------
  // DOM + fetch controller (browser only)
  // ---------------------------------------------------------------------------
  if (typeof document === 'undefined') return;

  function uuid() {
    if (global.crypto && global.crypto.randomUUID) return global.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'class') n.className = attrs[k];
      else if (k.indexOf('-') >= 0 || k === 'role' || k === 'tabindex') n.setAttribute(k, attrs[k]); // data-/aria-/role/tabindex
      else n[k] = attrs[k];
    });
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }

  // Tolerant JSON read: a transport error or non-JSON (e.g. a GAS HTML 500) yields null
  // instead of throwing, so the caller can show a friendly message rather than crashing.
  function parseJson(r) { return r.text().then(function (t) { try { return JSON.parse(t); } catch (e) { return null; } }); }

  // Inline !important styles sit at the TOP of the CSS cascade — they beat any theme
  // stylesheet rule, including the theme's own !important. We use this for the few layout
  // properties some themes hijack on <button>/containers (width, grid columns, gap, margin),
  // so the calendar tiles fill their columns (equal gaps) and the calendar+form stay capped.
  function forceStyle(node, styles) {
    Object.keys(styles).forEach(function (k) { node.style.setProperty(k, styles[k], 'important'); });
    return node;
  }
  function cap(px) { return { 'max-width': px, 'margin-left': 'auto', 'margin-right': 'auto', 'width': '100%', 'box-sizing': 'border-box' }; }
  var MOUNT_CAP = cap('620px'); // whole booker, centered
  var CAL_CAP = cap('460px');   // calendar — keeps tiles a moderate size
  var FORM_CAP = cap('560px');  // form — a bit wider/larger than the calendar
  var GRID_STYLE = { 'display': 'grid', 'grid-template-columns': 'repeat(7, 1fr)', 'gap': '14px', 'max-width': '100%' };
  var CELL_STYLE = { 'width': '100%', 'height': 'auto', 'aspect-ratio': '1 / 1', 'margin': '0', 'padding': '0', 'min-width': '0', 'max-width': 'none', 'box-sizing': 'border-box' };
  var INPUT_STYLE = { 'width': '100%', 'max-width': 'none', 'box-sizing': 'border-box' };

  function Controller(mount) {
    this.mount = mount;
    this.endpoint = mount.getAttribute('data-endpoint') || '';
    this.token = mount.getAttribute('data-token') || '';
    this.model = null;
    this.selectedDate = null;
    this.attemptId = null;        // one idempotency key per logical booking attempt
    this.posting = false;         // in-flight guard against re-entrant submits
    this._focusSelectable = false; // move focus to the calendar after a repick
    this.months = [];             // {year,month}[] across the horizon
    this.monthIndex = 0;          // which month the single-month view is showing
  }

  Controller.prototype.api = function (action) {
    return this.endpoint + (this.endpoint.indexOf('?') < 0 ? '?' : '&') +
      'action=' + encodeURIComponent(action) + '&token=' + encodeURIComponent(this.token);
  };

  Controller.prototype.start = function () {
    if (!this.endpoint || !this.token) { this.fatal('De planner is nog niet ingesteld.'); return; }
    // Seed a live status region BEFORE the first fetch so the loading state is actually
    // visible/announced (setStatus needs the node to exist).
    this.mount.innerHTML = '';
    forceStyle(this.mount, MOUNT_CAP); // lock our width past any theme rule
    this.mount.appendChild(el('div', { class: 'htb-status', id: 'htb-status', role: 'status', 'aria-live': 'polite', text: 'Beschikbare dagen laden…' }));
    this.loadAvailability();
  };

  Controller.prototype.loadAvailability = function () {
    var self = this;
    self.setStatus('Beschikbare dagen laden…');
    fetch(self.api('availability'), { method: 'GET' })
      .then(parseJson)
      .then(function (resp) {
        if (!resp || !resp.ok) { self.fatal(Booker.friendlyError(resp ? resp.reason : 'network')); return; }
        self.model = Booker.indexAvailability(resp);
        self.selectedDate = null;
        self.months = Booker.monthsInRange(self.model.today, self.model.horizonEnd);
        self.monthIndex = Booker.firstSelectableMonth(self.months, self.model.sel); // open on a useful month
        self.render();
      })
      .catch(function () { self.fatal(Booker.friendlyError('network')); });
  };

  Controller.prototype.render = function () {
    var self = this, m = self.model;
    self.mount.innerHTML = '';
    forceStyle(self.mount, MOUNT_CAP);
    self.mount.appendChild(el('div', { class: 'htb-status', id: 'htb-status', role: 'status', 'aria-live': 'polite' }));

    if (!self.months.length || !Object.keys(m.sel).length) {
      self.mount.appendChild(el('div', { class: 'htb-form-wrap', id: 'htb-form-wrap' }));
      self.setStatus('Er zijn de komende weken geen ophaaldagen beschikbaar. Kom binnenkort terug.');
      return;
    }
    if (self.monthIndex < 0) self.monthIndex = 0;
    if (self.monthIndex > self.months.length - 1) self.monthIndex = self.months.length - 1;
    var mm = self.months[self.monthIndex];

    // Month navigation: ‹  Maand JJJJ  ›  (one month at a time)
    var prev = el('button', { class: 'htb-nav', type: 'button', text: '‹', 'aria-label': 'Vorige maand' });
    var next = el('button', { class: 'htb-nav', type: 'button', text: '›', 'aria-label': 'Volgende maand' });
    prev.disabled = self.monthIndex === 0;
    next.disabled = self.monthIndex === self.months.length - 1;
    prev.addEventListener('click', function () { self.goMonth(-1); });
    next.addEventListener('click', function () { self.goMonth(1); });
    self.mount.appendChild(el('div', { class: 'htb-monthnav' }, [
      prev, el('div', { class: 'htb-month-name', text: Booker.MONTHS[mm.month - 1] + ' ' + mm.year }), next
    ]));

    var grid = el('div', { class: 'htb-month' });
    forceStyle(grid, CAL_CAP);
    var head = el('div', { class: 'htb-grid htb-weekdays' });
    forceStyle(head, GRID_STYLE);
    Booker.WEEKDAYS.forEach(function (w) { head.appendChild(el('div', { class: 'htb-wd', text: w })); });
    grid.appendChild(head);

    // ALL day cells live in ONE 7-column grid so `gap` spaces rows AND columns equally.
    // We SKIP the null padding cells and instead place the first day in its true weekday
    // column via grid-column-start; the rest auto-flow. (Empty padding divs were fragile —
    // they could collapse and shove the whole first row left.)
    var days = el('div', { class: 'htb-grid htb-days' });
    forceStyle(days, GRID_STYLE);
    var flat = Booker.monthGrid(mm.year, mm.month).reduce(function (a, wk) { return a.concat(wk); }, []);
    var placedFirst = false;
    flat.forEach(function (date, i) {
      if (date == null) return; // padding — render nothing
      var kind = Booker.classifyDate(date, m);
      var label = String(+date.slice(8, 10));
      var cell;
      if (kind === 'selectable') {
        var sel = m.sel[date];
        cell = el('button', { class: 'htb-cell htb-selectable', type: 'button', text: label, title: 'Ophalen ' + (sel.window_label || '') });
        cell.setAttribute('data-date', date);
        if (date === self.selectedDate) cell.className += ' htb-chosen';
        cell.addEventListener('click', function () { self.choose(date); });
      } else {
        cell = el('div', { class: 'htb-cell htb-' + kind, text: label });
      }
      forceStyle(cell, CELL_STYLE);
      if (!placedFirst) { cell.style.setProperty('grid-column-start', String((i % 7) + 1), 'important'); placedFirst = true; }
      days.appendChild(cell);
    });
    grid.appendChild(days);
    self.mount.appendChild(grid);

    self.mount.appendChild(el('div', { class: 'htb-legend' }, [
      el('span', { class: 'htb-lg htb-selectable', text: 'Beschikbaar' }),
      el('span', { class: 'htb-lg htb-blocked', text: 'Vol' }),
      el('span', { class: 'htb-lg htb-muted', text: 'Niet beschikbaar' })
    ]));
    self.mount.appendChild(forceStyle(el('div', { class: 'htb-form-wrap', id: 'htb-form-wrap' }), FORM_CAP));

    if (self._focusSelectable) { // after a repick, land the keyboard user on the calendar
      self._focusSelectable = false;
      var firstBtn = self.mount.querySelector('.htb-selectable');
      if (firstBtn && firstBtn.focus) firstBtn.focus();
    }
  };

  // Step to the prev/next month and keep an open form in view.
  Controller.prototype.goMonth = function (delta) {
    this.monthIndex += delta;
    this.render();
    if (this.selectedDate) this.showForm();
  };

  Controller.prototype.choose = function (date) {
    this.selectedDate = date;
    this.attemptId = uuid(); // fresh idempotency key per logical attempt; reused across retries of THIS booking
    this.render();
    this.showForm();
    var wrap = document.getElementById('htb-form-wrap');
    if (wrap && wrap.scrollIntoView) wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    var first = document.getElementById('htb-name'); // keyboard user lands on the first field
    if (first && first.focus) first.focus();
  };

  Controller.prototype.showForm = function () {
    var self = this, sel = self.model.sel[self.selectedDate];
    var wrap = document.getElementById('htb-form-wrap');
    wrap.innerHTML = '';
    wrap.appendChild(el('div', { class: 'htb-chosen-day', text: 'Gewenste ophaaldag: ' + Booker.formatDateNL(self.selectedDate) }));
    wrap.appendChild(el('div', { class: 'htb-chosen-sub', text: Booker.windowSentenceNL(sel.window_label || ('venster ' + sel.assignedWindow)) }));

    var form = el('form', { class: 'htb-form', id: 'htb-form' });
    var fields = [
      ['name', 'Naam', 'text', true, 'name'], ['email', 'E-mail', 'email', true, 'email'], ['phone', 'Telefoon', 'tel', true, 'tel'],
      ['address', 'Straat + huisnummer', 'text', true, 'street-address'], ['postcode', 'Postcode', 'text', true, 'postal-code'], ['city', 'Plaats', 'text', true, 'address-level2'],
      ['order_number', 'Ordernummer', 'text', false, 'off'], ['notes', 'Opmerkingen (optioneel)', 'text', false, 'off']
    ];
    fields.forEach(function (f) {
      var input = forceStyle(el(f[0] === 'notes' ? 'textarea' : 'input', { class: 'htb-input', id: 'htb-' + f[0], name: f[0], autocomplete: f[4] }), INPUT_STYLE);
      if (f[0] !== 'notes') input.type = f[2];
      input.placeholder = f[1] + (f[3] ? ' *' : '');
      form.appendChild(el('label', { class: 'htb-label' }, [el('span', { text: f[1] + (f[3] ? ' *' : '') }), input]));
    });
    var err = el('div', { class: 'htb-error', id: 'htb-error', role: 'alert', 'aria-live': 'assertive', tabindex: '-1' });
    var submit = el('button', { class: 'htb-submit', type: 'submit', text: 'Bevestig aanvraag' });
    form.appendChild(err); form.appendChild(submit);
    form.addEventListener('submit', function (e) { e.preventDefault(); self.submit(); });
    wrap.appendChild(form);
  };

  Controller.prototype.readForm = function () {
    var v = {};
    ['name', 'email', 'phone', 'address', 'postcode', 'city', 'order_number', 'notes'].forEach(function (k) {
      var node = document.getElementById('htb-' + k);
      v[k] = node ? node.value.trim() : '';
    });
    return v;
  };

  Controller.prototype.submit = function () {
    var self = this;
    if (self.posting) return; // re-entrancy guard (in addition to the disabled button)
    var v = self.readForm();
    var check = Booker.validateContact(v);
    var errBox = document.getElementById('htb-error');
    if (!check.ok) {
      errBox.textContent = check.invalidEmail ? Booker.friendlyError('invalid_email') : Booker.friendlyError('missing_fields');
      var badId = check.invalidEmail ? 'email' : check.missing[0];   // send the user straight to the problem
      var bad = document.getElementById('htb-' + badId);
      if (bad && bad.focus) bad.focus();
      return;
    }
    errBox.textContent = '';
    self.posting = true;
    var btn = self.mount.querySelector('.htb-submit');
    if (btn) { btn.disabled = true; btn.textContent = 'Bezig…'; }

    // Reuse the per-attempt idempotency key so a retry after a lost response can't double-book
    // (the server collapses a repeat attempt_id to {duplicate:true}).
    var payload = Booker.buildBookingPayload(v, self.selectedDate, self.token, self.attemptId || uuid());
    fetch(self.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' }, // simple request — no preflight
      body: JSON.stringify(payload)
    })
      .then(parseJson)
      .then(function (res) {
        if (res && res.ok) { self.confirm(res.booking); return; } // success replaces the view
        if (res && res.revalidate) {
          // Slot filled between load and submit. Keep the control LOCKED through the refresh
          // so the user can't fire another POST at the now-stale date; render() rebuilds the form.
          if (btn) { btn.textContent = 'Vernieuwen…'; }
          errBox.textContent = Booker.friendlyError(res.reason) + ' Beschikbare dagen worden vernieuwd…';
          self._focusSelectable = true;
          setTimeout(function () { self.posting = false; self.loadAvailability(); }, 1200);
          return;
        }
        self.posting = false;
        if (btn) { btn.disabled = false; btn.textContent = 'Bevestig aanvraag'; }
        errBox.textContent = Booker.friendlyError(res ? res.reason : 'busy');
      })
      .catch(function () {
        self.posting = false;
        if (btn) { btn.disabled = false; btn.textContent = 'Bevestig aanvraag'; }
        errBox.textContent = Booker.friendlyError('network');
      });
  };

  Controller.prototype.confirm = function (booking) {
    this.mount.innerHTML = '';
    var box = el('div', { class: 'htb-confirm', role: 'status', 'aria-live': 'polite', tabindex: '-1' }, [
      el('div', { class: 'htb-confirm-check', text: '✓' }),
      el('h3', { text: 'Je aanvraag is ontvangen' }),
      el('p', { text: Booker.formatDateNL(booking.date) }),
      el('p', { text: Booker.windowSentenceNL(booking.window_label || ('venster ' + booking.window)) }),
      booking.order_number ? el('p', { text: 'Ordernummer: ' + booking.order_number }) : null,
      el('p', { class: 'htb-confirm-note', text: 'Je ontvangt een bevestiging per e-mail. We kunnen nog contact met je opnemen om de dag of tijd te bevestigen of aan te passen. Referentie: ' + booking.booking_id })
    ]);
    this.mount.appendChild(box);
    if (box.focus) box.focus();
  };

  Controller.prototype.setStatus = function (msg) { var s = document.getElementById('htb-status'); if (s) s.textContent = msg; };
  Controller.prototype.fatal = function (msg) { this.mount.innerHTML = ''; this.mount.appendChild(el('div', { class: 'htb-status htb-fatal', text: msg })); };

  function boot() {
    var mounts = document.querySelectorAll('.htb-mount[data-endpoint]');
    Array.prototype.forEach.call(mounts, function (mount) {
      if (mount.getAttribute('data-booted')) return;
      mount.setAttribute('data-booted', '1');
      new Controller(mount).start();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  global.HiTapesBooker.boot = boot;

})(typeof window !== 'undefined' ? window : this);